'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

// ── City data ──────────────────────────────────────────────────────────────────
interface CityPreview {
  id: string;
  name: string;
  arabicName: string;
  color: string;
  accentColor: string;
  description: string;
  keyWard: string;
  wardSeverity: 'CRITICAL' | 'HIGH' | 'MODERATE';
  wardStats: { label: string; value: string; icon: string }[];
  masajid: string[];
  topResources: { name: string; category: string; icon: string }[];
  ayat: { arabic: string; ref: string; nameOfAllah: string; arabicName: string; meaning: string };
  openLetterQuote: string;
  tags: string[];
  wardCount: number;
  observationCount?: number;
}

const CITY_PREVIEWS: CityPreview[] = [
  {
    id: 'chicago',
    name: 'Chicago',
    arabicName: 'شيكاغو',
    color: '#1C2B25',
    accentColor: '#7EC8A0',
    description: 'IMAN\'s birthplace. Englewood, South Shore, Austin — the full weight of the Great Migration, redlining, and Muslim community development.',
    keyWard: 'Englewood',
    wardSeverity: 'CRITICAL',
    wardStats: [
      { label: 'Food Desert', value: '85%', icon: '🌿' },
      { label: 'Vacancy', value: '55%', icon: '🏚' },
      { label: 'Poverty', value: '45%', icon: '⚖' },
    ],
    masajid: ['IMAN Chicago HQ', 'Masjid Al-Fatir', 'Masjid Al-Taqwa', 'Mosque Maryam (NOI)', 'Masjid Al-Faatir'],
    topResources: [
      { name: 'IMAN — Inner-City Muslim Action Network', category: 'Community', icon: '⬡' },
      { name: 'IMAN Green ReEntry', category: 'Reentry', icon: '🌱' },
      { name: 'Greater Chicago Food Depository', category: 'Food', icon: '🌿' },
      { name: 'Heartland Alliance', category: 'Housing', icon: '🏘' },
      { name: 'Chicago Legal Aid', category: 'Justice', icon: '⚖' },
      { name: 'Lawndale Christian Health Center', category: 'Health', icon: '🤲' },
    ],
    ayat: {
      arabic: 'وَمَا مِن دَابَّةٍ فِي الْأَرْضِ إِلَّا عَلَى اللَّهِ رِزْقُهَا',
      ref: '11:6',
      nameOfAllah: 'AR-RAZZAQ',
      arabicName: 'الرَّزَّاق',
      meaning: 'The Ever-Provider',
    },
    openLetterQuote: 'IMAN was born in Englewood — from the community, for the community. The Amanah is to be the roots that hold the soil.',
    tags: ['IMAN', 'Green ReEntry', 'Food Justice', 'South Side'],
    wardCount: 8,
  },
  {
    id: 'atlanta',
    name: 'Atlanta',
    arabicName: 'أطلانتا',
    color: '#1A1200',
    accentColor: '#C9A84C',
    description: 'Heart of the Black Belt South. English Avenue, West End, East Lake — redlining maps that still govern food deserts today.',
    keyWard: 'English Ave / Vine City',
    wardSeverity: 'CRITICAL',
    wardStats: [
      { label: 'Food Desert', value: '90%', icon: '🌿' },
      { label: 'Vacancy', value: '52%', icon: '🏚' },
      { label: 'Poverty', value: '49%', icon: '⚖' },
    ],
    masajid: ['Community Masjid of Atlanta (est. 1976)', 'Atlanta Masjid of Al-Islam', 'IMAN Atlanta', 'Masjid Al-Muminun', 'Masjid Al-Falah'],
    topResources: [
      { name: 'Community Masjid of Atlanta', category: 'Community', icon: '🕌' },
      { name: 'Westside Future Fund', category: 'Housing', icon: '🏘' },
      { name: 'Atlanta Community Food Bank', category: 'Food', icon: '🌿' },
    ],
    ayat: {
      arabic: 'كُونُوا قَوَّامِينَ بِالْقِسْطِ شُهَدَاءَ لِلَّهِ',
      ref: '4:135',
      nameOfAllah: 'AL-MUQSIT',
      arabicName: 'الْمُقْسِط',
      meaning: 'The Equitable',
    },
    openLetterQuote: 'We were commanded to come down for a time as Caretakers — to see what is broken and to bridge it.',
    tags: ['BeltLine', 'Anti-Displacement', 'West End', 'Caretaking'],
    wardCount: 8,
  },
  {
    id: 'portland',
    name: 'Portland',
    arabicName: 'بورتلاند',
    color: '#0A1420',
    accentColor: '#6BA8C4',
    description: 'Two catastrophic displacements — Vanport 1948, Alberta gentrification 2000s. Masjid As-Saber (The Patient) stands in the displacement zone.',
    keyWard: 'East Portland / 82nd Ave',
    wardSeverity: 'CRITICAL',
    wardStats: [
      { label: 'Gentrification', value: '92%', icon: '🏗' },
      { label: 'Food Desert', value: '78%', icon: '🌿' },
      { label: 'Poverty', value: '40%', icon: '⚖' },
    ],
    masajid: ['Masjid As-Saber', 'Bilal Masjid (MLK Blvd)', 'Masjid Al-Nur', 'Islamic Society of Greater Portland'],
    topResources: [
      { name: 'Masjid As-Saber', category: 'Community', icon: '🕌' },
      { name: 'Oregon Food Bank', category: 'Food', icon: '🌿' },
      { name: 'Hacienda CDC', category: 'Housing', icon: '🏘' },
    ],
    ayat: {
      arabic: 'وَاللَّهُ جَعَلَ لَكُم مِّن بُيُوتِكُمْ سَكَنًا',
      ref: '16:80',
      nameOfAllah: 'AL-WALI',
      arabicName: 'الْوَلِيّ',
      meaning: 'The Protecting Friend',
    },
    openLetterQuote: '82nd Avenue is the displacement terminus. The name of the masjid is the Ayah — As-Saber, The Patient.',
    tags: ['Vanport', 'Displacement', 'Refugee', 'As-Saber'],
    wardCount: 8,
  },
  {
    id: 'detroit',
    name: 'Detroit',
    arabicName: 'ديترويت',
    color: '#1A0A00',
    accentColor: '#C4956A',
    description: 'Hamtramck — first all-Muslim city council. Brightmoor Farmway — food growing where houses stood. The oldest masjid in Michigan endures.',
    keyWard: 'Hamtramck',
    wardSeverity: 'HIGH',
    wardStats: [
      { label: 'Vacancy', value: '55%', icon: '🏚' },
      { label: 'Food Desert', value: '85%', icon: '🌿' },
      { label: 'Poverty', value: '48%', icon: '⚖' },
    ],
    masajid: ['American Moslem Society (est. 1938)', 'Masjid Wali Muhammad', 'Hamtramck Islamic Center', 'Islamic House of Wisdom', 'Masjid Al-Haqq'],
    topResources: [
      { name: 'American Moslem Society', category: 'Community', icon: '🕌' },
      { name: 'Brightmoor Farmway', category: 'Food', icon: '🌱' },
      { name: 'ACCESS — Arab Community Center', category: 'Community', icon: '⬡' },
    ],
    ayat: {
      arabic: 'قُلْ يَا عِبَادِيَ الَّذِينَ أَسْرَفُوا عَلَىٰ أَنفُسِهِمْ لَا تَقْنَطُوا مِن رَّحْمَةِ اللَّهِ',
      ref: '39:53',
      nameOfAllah: 'AL-AFUWW',
      arabicName: 'الْعَفُوّ',
      meaning: 'The Pardoner',
    },
    openLetterQuote: 'If the Hour is established and one has a palm shoot, let him plant it. Brightmoor Farmway is this Hadith made operational.',
    tags: ['Hamtramck', 'Urban Farming', 'Deindustrialization', 'Al-Baqi'],
    wardCount: 8,
  },
];

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: '#E05252',
  HIGH: '#C9A84C',
  MODERATE: '#7EC8A0',
};

const CARETAKING_DOMAINS = [
  { name: 'AL-HAFIZ', meaning: 'The Preserver', domain: 'HOUSING & SHELTER', icon: '🏘', color: '#6BA8C4', desc: 'Housing is not just shelter — it is the preservation of dignity, family, and the conditions for worship. Every roof is a sign of Al-Hafiz.', items: ['Emergency housing', 'Affordable housing advocacy', 'Tenant rights', 'Transitional housing', 'Home ownership pathways'] },
  { name: 'AL-ALIM', meaning: 'The All-Knowing', domain: 'EDUCATION & ILM', icon: '📚', color: '#C9A84C', desc: 'Education is a channel of the All-Knowing. Every school, every teacher, every book is an Ayah of Al-Alim.', items: ['Islamic schools', 'Homeschool networks', 'Adult education', 'Quran programs', 'Scholarship funds'] },
  { name: 'AR-RAZZAQ', meaning: 'The Ever-Provider', domain: 'FOOD & PROVISION', icon: '🌿', color: '#7EC8A0', desc: 'Food banks, halal food access, community gardens — all are expressions of Ar-Razzaq\'s provision flowing through human hands.', items: ['Food banks', 'Halal food access', 'Community gardens', 'Meal programs', 'Nutrition education'] },
  { name: 'AS-SALAM', meaning: 'The Source of Peace', domain: 'MENTAL HEALTH', icon: '🤲', color: '#8a8ab8', desc: 'Peace is not the absence of difficulty — it is the presence of As-Salam. Mental health resources restore the conditions of inner peace.', items: ['Muslim counselors', 'Crisis support', 'Community circles', 'Grief support', 'Youth programs'] },
  { name: 'AL-ADL', meaning: 'The Just', domain: 'LEGAL & RIGHTS', icon: '⚖', color: '#C4956A', desc: 'Justice is not a human invention — it is a Name of Allah. Legal aid, civil rights, advocacy — all are expressions of Al-Adl.', items: ['Legal aid', 'Civil rights advocacy', 'Immigration support', 'Prisoner support', 'Anti-discrimination'] },
  { name: 'AL-MUHAYMIN', meaning: 'The Guardian', domain: 'COMMUNITY SAFETY', icon: '🛡', color: '#E05252', desc: 'The Guardian watches over. Community safety programs, conflict resolution, and neighborhood care are expressions of Al-Muhaymin.', items: ['Neighborhood watch', 'Conflict resolution', 'Youth mentorship', 'Anti-violence programs', 'Community policing alternatives'] },
];

const CHICAGO_CONTEXT = {
  history: 'Chicago\'s South and West Sides carry the full weight of the Great Migration — 1.6 million Black Americans who moved north between 1910 and 1970, many settling in neighborhoods that would be systematically redlined, disinvested, and abandoned. Englewood, Austin, Lawndale, South Shore: these are not "underserved" communities — they are communities that were actively served with harm.',
  imanContext: 'IMAN (Inner-City Muslim Action Network) was founded in 1997 by Dr. Rami Nashashibi in Marquette Park, Chicago. It is the organizational expression of the Muslim Resource Guide concept — a community-rooted institution that understands resources as Amanah, not charity. IMAN\'s Green ReEntry program, health clinics, and arts programming are the MRG made operational.',
  irlLens: 'The IRL Lens in Chicago begins with the Zahir: the visible vacancy rates, the food deserts, the distance between masajid and resources. The Batin: the redlining maps from 1940 that still govern where grocery stores are built. The Haqq: every community member has a right to provision, shelter, health, and justice — Al-Razzaq, Al-Hafiz, As-Salam, Al-Adl are all active here.',
};

const CHICAGO_WARD_SEVERITIES: ('CRITICAL' | 'HIGH' | 'MODERATE')[] = [
  'CRITICAL', 'CRITICAL', 'HIGH', 'CRITICAL', 'HIGH', 'MODERATE', 'HIGH', 'CRITICAL'
];

const CHICAGO_WARD_NAMES = [
  'Englewood', 'West Englewood', 'Austin', 'Lawndale', 'South Shore', 'Woodlawn', 'Chatham', 'Roseland'
];

function useCityObservationCounts() {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const { supabase } = await import('@/lib/supabaseClient');
        const { data: obsData } = await supabase
          .from('irl_observations')
          .select('city')
          .in('city', ['Chicago', 'Atlanta', 'Portland', 'Detroit']);
        if (obsData) {
          const countMap: Record<string, number> = {};
          obsData.forEach((row: { city: string }) => {
            const key = row.city.toLowerCase();
            countMap[key] = (countMap[key] || 0) + 1;
          });
          setCounts(countMap);
        }
      } catch { /* silent fail */ }
      setLoading(false);
    }
    fetchData();
  }, []);

  return { counts, loading };
}

// ── Chicago Full MRG Experience ───────────────────────────────────────────────

function ChicagoMRGExperience() {
  const [activeTab, setActiveTab] = useState<'ayat' | 'resources' | 'domains' | 'context' | 'irl'>('ayat');
  const [activeDomain, setActiveDomain] = useState(0);
  const [activeWard, setActiveWard] = useState<number | null>(null);
  const city = CITY_PREVIEWS[0]; // Chicago

  const tabs = [
    { id: 'ayat' as const, label: 'Ayat', icon: '✦', desc: 'Signs & Names of Allah' },
    { id: 'resources' as const, label: 'Resources', icon: '⬡', desc: 'Full resource directory' },
    { id: 'domains' as const, label: 'Caretaking Domains', icon: '◈', desc: 'By Name of Allah' },
    { id: 'context' as const, label: 'Context', icon: '◎', desc: 'Historical & community' },
    { id: 'irl' as const, label: 'IRL Lens', icon: '◉', desc: 'Zahir · Batin · Haqq' },
  ];

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #0A1410 0%, #1C2B25 60%, #0F1E18 100%)', border: '1px solid rgba(126,200,160,0.25)' }}>

      {/* Chicago Header */}
      <div className="px-6 pt-6 pb-4" style={{ borderBottom: '1px solid rgba(126,200,160,0.15)' }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-xs px-3 py-1 rounded" style={{ background: 'rgba(126,200,160,0.15)', color: '#7EC8A0', border: '1px solid rgba(126,200,160,0.3)', fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                ◉ Live · Chicago, IL
              </span>
              <span className="font-mono text-xs px-2 py-1 rounded" style={{ background: 'rgba(224,82,82,0.15)', color: '#E05252', border: '1px solid rgba(224,82,82,0.3)', fontSize: '0.55rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                CRITICAL
              </span>
            </div>
            <h3 className="font-heading mb-1" style={{ fontSize: 'clamp(1.2rem, 2.5vw, 1.6rem)', color: '#F5F0E8', fontWeight: 600, lineHeight: 1.2 }}>
              شيكاغو · Chicago
            </h3>
            <p className="font-body text-sm" style={{ color: 'rgba(245,240,232,0.65)', maxWidth: 480, lineHeight: 1.7 }}>
              IMAN's birthplace. The Muslim Resource Guide begins here — where the Great Migration, redlining, and Muslim community development converge in Englewood, Austin, and the South Side.
            </p>
          </div>
          <div className="flex flex-col gap-2 flex-shrink-0">
            <div className="text-right">
              <div className="font-mono text-2xl font-bold" style={{ color: '#7EC8A0' }}>8</div>
              <div className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.35)', fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Wards Mapped</div>
            </div>
            <div className="text-right">
              <div className="font-mono text-2xl font-bold" style={{ color: '#C9A84C' }}>5</div>
              <div className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.35)', fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Masajid</div>
            </div>
          </div>
        </div>

        {/* Ward Tile Mosaic */}
        <div className="mt-4">
          <p className="font-mono text-xs mb-2" style={{ color: 'rgba(245,240,232,0.25)', fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Ward Tile Mosaic · Severity Map
          </p>
          <div className="flex gap-1.5 flex-wrap">
            {CHICAGO_WARD_SEVERITIES.map((sev, i) => (
              <button
                key={i}
                onClick={() => setActiveWard(activeWard === i ? null : i)}
                className="flex-shrink-0 rounded transition-all duration-200 relative group"
                title={`${CHICAGO_WARD_NAMES[i]}: ${sev}`}
                style={{
                  width: `${28 + (i % 3) * 10}px`,
                  height: `${22 + (i % 4) * 6}px`,
                  background: SEVERITY_COLORS[sev],
                  opacity: activeWard === null ? 0.5 + (i % 3) * 0.15 : activeWard === i ? 1 : 0.25,
                  border: activeWard === i ? `2px solid ${SEVERITY_COLORS[sev]}` : '2px solid transparent',
                  boxShadow: activeWard === i ? `0 0 12px ${SEVERITY_COLORS[sev]}60` : 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = activeWard === i ? '1' : activeWard === null ? String(0.5 + (i % 3) * 0.15) : '0.25'; }}
              />
            ))}
          </div>
          {activeWard !== null && (
            <div className="mt-2 px-3 py-2 rounded" style={{ background: `${SEVERITY_COLORS[CHICAGO_WARD_SEVERITIES[activeWard]]}15`, border: `1px solid ${SEVERITY_COLORS[CHICAGO_WARD_SEVERITIES[activeWard]]}30` }}>
              <span className="font-mono text-xs font-bold" style={{ color: SEVERITY_COLORS[CHICAGO_WARD_SEVERITIES[activeWard]], fontSize: '0.7rem' }}>
                {CHICAGO_WARD_NAMES[activeWard]}
              </span>
              <span className="font-mono text-xs ml-2" style={{ color: 'rgba(245,240,232,0.4)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {CHICAGO_WARD_SEVERITIES[activeWard]}
              </span>
            </div>
          )}
          <p className="font-mono mt-2" style={{ fontSize: '0.5rem', color: 'rgba(245,240,232,0.2)', letterSpacing: '0.06em' }}>
            <span style={{ color: '#E05252' }}>■</span> CRITICAL &nbsp;
            <span style={{ color: '#C9A84C' }}>■</span> HIGH &nbsp;
            <span style={{ color: '#7EC8A0' }}>■</span> MODERATE &nbsp;· tap a tile to identify
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex overflow-x-auto" style={{ borderBottom: '1px solid rgba(126,200,160,0.12)', background: 'rgba(0,0,0,0.2)' }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="flex-shrink-0 flex flex-col items-center gap-0.5 px-4 py-3 transition-all duration-200"
            style={{
              background: activeTab === tab.id ? 'rgba(126,200,160,0.1)' : 'transparent',
              borderBottom: activeTab === tab.id ? '2px solid #7EC8A0' : '2px solid transparent',
              color: activeTab === tab.id ? '#7EC8A0' : 'rgba(245,240,232,0.35)',
            }}
          >
            <span style={{ fontSize: 14 }}>{tab.icon}</span>
            <span className="font-mono text-xs font-bold" style={{ fontSize: '0.6rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-5">

        {/* ── AYAT TAB ──────────────────────────────────────────────────────── */}
        {activeTab === 'ayat' && (
          <div className="space-y-4">
            <p className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.3)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Ayat in Chicago — Signs pointing to the Names of Allah
            </p>

            {/* Primary Ayat */}
            <div className="p-5 rounded-xl" style={{ background: 'rgba(28,43,37,0.6)', border: '1px solid rgba(126,200,160,0.25)' }}>
              <div className="flex items-center gap-2 mb-3">
                <span className="font-mono text-xs px-2 py-1 rounded" style={{ background: 'rgba(126,200,160,0.15)', color: '#7EC8A0', border: '1px solid rgba(126,200,160,0.3)', fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {city.ayat.nameOfAllah} · {city.ayat.arabicName}
                </span>
                <span className="font-mono text-xs" style={{ color: 'rgba(126,200,160,0.5)', fontSize: '0.55rem' }}>{city.ayat.meaning}</span>
              </div>
              <p className="font-heading mb-3 text-right leading-relaxed" style={{ fontSize: 'clamp(1rem, 2.2vw, 1.3rem)', color: '#F5F0E8', direction: 'rtl', lineHeight: 1.9, fontFamily: "'Amiri', serif" }}>
                {city.ayat.arabic}
              </p>
              <p className="font-mono text-xs mb-3" style={{ color: '#7EC8A0', fontSize: '0.6rem', letterSpacing: '0.06em' }}>
                Quran {city.ayat.ref} · {city.ayat.meaning}
              </p>
              <p className="font-body italic" style={{ fontSize: '0.8rem', color: 'rgba(245,240,232,0.45)', lineHeight: 1.7, borderLeft: '2px solid rgba(126,200,160,0.4)', paddingLeft: 12 }}>
                "{city.openLetterQuote}"
              </p>
            </div>

            {/* Additional Ayat for Chicago */}
            {[
              { arabic: 'إِنَّ اللَّهَ يَأْمُرُ بِالْعَدْلِ وَالْإِحْسَانِ', ref: '16:90', name: 'AL-ADL', arabicName: 'الْعَدْل', meaning: 'The Just', context: 'The Mizan is out of balance in Englewood. Al-Adl commands justice — the MRG is the response.' },
              { arabic: 'وَتَعَاوَنُوا عَلَى الْبِرِّ وَالتَّقْوَىٰ', ref: '5:2', name: 'AL-WADUD', arabicName: 'الْوَدُود', meaning: 'The Loving', context: 'Cooperation in righteousness — IMAN\'s model of community-rooted caretaking.' },
              { arabic: 'مَن كَانَ فِي حَاجَةِ أَخِيهِ كَانَ اللَّهُ فِي حَاجَتِهِ', ref: 'Hadith', name: 'AR-RAHMAN', arabicName: 'الرَّحْمَٰن', meaning: 'The Most Compassionate', context: 'Whoever is in the need of his brother, Allah is in his need. The MRG is this Hadith made operational.' },
            ].map((ayat, i) => (
              <div key={i} className="p-4 rounded-xl" style={{ background: 'rgba(28,43,37,0.4)', border: '1px solid rgba(44,74,62,0.3)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-mono text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(126,200,160,0.1)', color: '#7EC8A0', border: '1px solid rgba(126,200,160,0.2)', fontSize: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {ayat.name} · {ayat.arabicName}
                  </span>
                  <span className="font-mono text-xs" style={{ color: 'rgba(126,200,160,0.4)', fontSize: '0.5rem' }}>{ayat.ref}</span>
                </div>
                <p className="text-right mb-2" style={{ fontFamily: "'Amiri', serif", fontSize: 15, color: 'rgba(201,168,76,0.8)', direction: 'rtl', lineHeight: 1.8 }}>
                  {ayat.arabic}
                </p>
                <p className="font-body text-xs italic" style={{ color: 'rgba(245,240,232,0.4)', lineHeight: 1.6 }}>{ayat.context}</p>
              </div>
            ))}

            {/* Citywide resource list under Ayat */}
            <div className="p-4 rounded-xl" style={{ background: 'rgba(15,30,24,0.8)', border: '1px solid rgba(44,74,62,0.35)' }}>
              <p className="font-mono text-xs mb-3" style={{ color: '#7EC8A0', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Citywide Resources — All Wards · Chicago
              </p>
              <div className="space-y-2">
                {city.topResources.map((res) => (
                  <div key={res.name} className="flex items-center justify-between p-2 rounded" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(44,74,62,0.2)' }}>
                    <div className="flex items-center gap-2">
                      <span style={{ fontSize: 14 }}>{res.icon}</span>
                      <span className="font-body text-sm" style={{ color: 'rgba(245,240,232,0.7)', fontSize: '0.8rem' }}>{res.name}</span>
                    </div>
                    <span className="font-mono text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(126,200,160,0.1)', color: '#7EC8A0', fontSize: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {res.category}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── RESOURCES TAB ─────────────────────────────────────────────────── */}
        {activeTab === 'resources' && (
          <div className="space-y-4">
            <p className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.3)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Full Resources — Chicago · All Wards
            </p>

            {/* Masajid */}
            <div className="p-4 rounded-xl" style={{ background: 'rgba(28,43,37,0.5)', border: '1px solid rgba(44,74,62,0.35)' }}>
              <p className="font-mono text-xs mb-3" style={{ color: '#7EC8A0', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                🕌 Masajid · {city.masajid.length} Documented
              </p>
              <div className="space-y-1.5">
                {city.masajid.map((m) => (
                  <div key={m} className="flex items-center gap-2 p-2 rounded" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(44,74,62,0.2)' }}>
                    <span style={{ color: '#7EC8A0', fontSize: 12 }}>🕌</span>
                    <span className="font-body text-sm" style={{ color: 'rgba(245,240,232,0.7)', fontSize: '0.8rem' }}>{m}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Resources by category */}
            {[
              { cat: 'Community & Mutual Aid', icon: '⬡', color: '#7EC8A0', items: ['IMAN — Inner-City Muslim Action Network', 'IMAN Green ReEntry', 'Chicago Urban League', 'Southside Together Organizing for Power (STOP)', 'Blocks Together'] },
              { cat: 'Food & Provision', icon: '🌿', color: '#C9A84C', items: ['Greater Chicago Food Depository', 'Chicago Food Bank Network', 'IMAN Community Kitchen', 'Englewood Community Garden Network', 'Halal Food Access Initiative'] },
              { cat: 'Housing & Shelter', icon: '🏘', color: '#6BA8C4', items: ['Heartland Alliance', 'Chicago Coalition for the Homeless', 'Lawndale Christian Development Corp', 'Affordable Housing Action Coalition', 'Tenant Rights Chicago'] },
              { cat: 'Health & Wellbeing', icon: '🤲', color: '#8a8ab8', items: ['Lawndale Christian Health Center', 'IMAN Health Clinic', 'Chicago Department of Public Health', 'Muslim Mental Health Initiative', 'Community Health Workers Network'] },
              { cat: 'Justice & Legal Aid', icon: '⚖', color: '#C4956A', items: ['Chicago Legal Aid', 'Cabrini Green Legal Aid', 'IMAN Justice Program', 'Restore Justice Illinois', 'Chicago Appleseed'] },
            ].map((section) => (
              <div key={section.cat} className="p-4 rounded-xl" style={{ background: 'rgba(15,30,24,0.7)', border: `1px solid ${section.color}20` }}>
                <p className="font-mono text-xs mb-3" style={{ color: section.color, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {section.icon} {section.cat}
                </p>
                <div className="space-y-1.5">
                  {section.items.map((item) => (
                    <div key={item} className="flex items-center gap-2 p-2 rounded" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(44,74,62,0.15)' }}>
                      <span style={{ color: section.color, fontSize: 8 }}>◉</span>
                      <span className="font-body text-sm" style={{ color: 'rgba(245,240,232,0.65)', fontSize: '0.78rem' }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <Link
              href="/muslim-resource-guide?city=chicago"
              className="group flex items-center justify-between p-4 rounded-xl transition-all duration-200"
              style={{ background: 'rgba(126,200,160,0.08)', border: '1px solid rgba(126,200,160,0.25)', textDecoration: 'none', minHeight: '48px' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(126,200,160,0.15)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(126,200,160,0.08)'; }}
            >
              <span className="font-body text-sm font-semibold" style={{ color: '#7EC8A0' }}>View full Chicago resource guide →</span>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="transition-transform duration-300 group-hover:translate-x-1">
                <path d="M2 7h10M8 3l4 4-4 4" stroke="#7EC8A0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>
        )}

        {/* ── CARETAKING DOMAINS TAB ────────────────────────────────────────── */}
        {activeTab === 'domains' && (
          <div className="space-y-4">
            <p className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.3)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Caretaking Domains — Each framed by a Name of Allah
            </p>
            <p className="font-body text-sm" style={{ color: 'rgba(245,240,232,0.45)', lineHeight: 1.7, fontSize: '0.82rem' }}>
              Every domain of community need is understood as a channel through which a Name of Allah operates. The caretaker who serves in housing is a vessel of Al-Hafiz. The one who feeds is a vessel of Ar-Razzaq.
            </p>

            {/* Domain selector */}
            <div className="flex flex-wrap gap-2">
              {CARETAKING_DOMAINS.map((d, i) => (
                <button
                  key={d.name}
                  onClick={() => setActiveDomain(i)}
                  className="font-mono text-xs px-3 py-2 rounded transition-all duration-200"
                  style={{
                    background: activeDomain === i ? `${d.color}20` : 'rgba(28,43,37,0.4)',
                    border: `1px solid ${activeDomain === i ? d.color + '50' : 'rgba(44,74,62,0.3)'}`,
                    color: activeDomain === i ? d.color : 'rgba(245,240,232,0.4)',
                    fontSize: '0.6rem',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  {d.icon} {d.name}
                </button>
              ))}
            </div>

            {/* Active domain */}
            {(() => {
              const d = CARETAKING_DOMAINS[activeDomain];
              return (
                <div className="p-5 rounded-xl" style={{ background: `${d.color}08`, border: `1px solid ${d.color}25` }}>
                  <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-xs font-bold" style={{ color: d.color, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{d.name}</span>
                        <span className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.4)', fontSize: '0.6rem' }}>{d.meaning}</span>
                      </div>
                      <p className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.5)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{d.domain}</p>
                    </div>
                    <span style={{ fontSize: 24 }}>{d.icon}</span>
                  </div>
                  <p className="font-body text-sm mb-4" style={{ color: 'rgba(245,240,232,0.65)', lineHeight: 1.8, fontSize: '0.85rem' }}>{d.desc}</p>
                  <div className="space-y-1.5">
                    {d.items.map((item) => (
                      <div key={item} className="flex items-center gap-2 p-2 rounded" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${d.color}15` }}>
                        <span style={{ color: d.color, fontSize: 8 }}>◉</span>
                        <span className="font-body text-sm" style={{ color: 'rgba(245,240,232,0.6)', fontSize: '0.78rem' }}>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── CONTEXT TAB ───────────────────────────────────────────────────── */}
        {activeTab === 'context' && (
          <div className="space-y-4">
            <p className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.3)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Context — Chicago · Historical & Community
            </p>

            {[
              { title: 'Historical Context', icon: '📜', color: '#C9A84C', content: CHICAGO_CONTEXT.history },
              { title: 'IMAN & The MRG', icon: '⬡', color: '#7EC8A0', content: CHICAGO_CONTEXT.imanContext },
            ].map((section) => (
              <div key={section.title} className="p-5 rounded-xl" style={{ background: 'rgba(28,43,37,0.5)', border: `1px solid ${section.color}20`, borderLeft: `3px solid ${section.color}40` }}>
                <div className="flex items-center gap-2 mb-3">
                  <span style={{ fontSize: 16 }}>{section.icon}</span>
                  <p className="font-mono text-xs font-bold" style={{ color: section.color, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{section.title}</p>
                </div>
                <p className="font-body text-sm" style={{ color: 'rgba(245,240,232,0.65)', lineHeight: 1.8, fontSize: '0.85rem' }}>{section.content}</p>
              </div>
            ))}

            {/* Ward stats */}
            <div className="p-4 rounded-xl" style={{ background: 'rgba(15,30,24,0.7)', border: '1px solid rgba(44,74,62,0.3)' }}>
              <p className="font-mono text-xs mb-3" style={{ color: '#7EC8A0', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Key Ward — Englewood · Disparity Data
              </p>
              <div className="grid grid-cols-3 gap-2">
                {city.wardStats.map((stat) => (
                  <div key={stat.label} className="p-3 rounded text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ fontSize: 18, marginBottom: 4 }}>{stat.icon}</div>
                    <div className="font-mono font-bold" style={{ fontSize: '0.9rem', color: '#7EC8A0' }}>{stat.value}</div>
                    <div className="font-mono" style={{ fontSize: '0.5rem', color: 'rgba(245,240,232,0.35)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {city.tags.map((tag) => (
                <span key={tag} className="font-mono px-3 py-1 rounded" style={{ background: 'rgba(126,200,160,0.1)', border: '1px solid rgba(126,200,160,0.2)', color: '#7EC8A0', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ── IRL LENS TAB ──────────────────────────────────────────────────── */}
        {activeTab === 'irl' && (
          <div className="space-y-4">
            <p className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.3)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              IRL Lens — Chicago · Zahir · Batin · Haqq
            </p>

            <div className="p-5 rounded-xl" style={{ background: 'rgba(28,43,37,0.5)', border: '1px solid rgba(45,138,135,0.25)', borderLeft: '3px solid rgba(45,138,135,0.5)' }}>
              <p className="font-mono text-xs mb-3" style={{ color: '#2d8a87', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>◎ IRL Lens Application</p>
              <p className="font-body text-sm" style={{ color: 'rgba(245,240,232,0.65)', lineHeight: 1.8, fontSize: '0.85rem' }}>{CHICAGO_CONTEXT.irlLens}</p>
            </div>

            {[
              { layer: 'Zahir', arabic: 'الظَّاهِر', color: '#C9A84C', desc: 'The visible surface of Chicago\'s South Side: vacancy rates, food deserts, distance between masajid and resources. What the eye sees when walking Englewood.', prompt: 'Walk one block in Englewood. Count the vacant lots. Count the liquor stores. Count the grocery stores. This is the Zahir.' },
              { layer: 'Batin', arabic: 'الْبَاطِن', color: '#8a8ab8', desc: 'The hidden structure beneath the visible: the 1940 redlining maps that still govern where grocery stores are built. The policy decisions that created the food desert. The structural injustice beneath the surface.', prompt: 'Look up the 1940 HOLC redlining map for Chicago. Overlay it with today\'s food desert map. The Batin is visible.' },
              { layer: 'Haqq', arabic: 'الْحَقّ', color: '#7EC8A0', desc: 'The truth, right, and due of every community member: provision (Ar-Razzaq), shelter (Al-Hafiz), peace (As-Salam), justice (Al-Adl). The MRG is the map of these rights.', prompt: 'Every resource in the MRG is a Haqq — a right, not a charity. The caretaker who connects a community member to a resource is fulfilling the Amanah.' },
            ].map((layer) => (
              <div key={layer.layer} className="p-4 rounded-xl" style={{ background: 'rgba(15,30,24,0.7)', border: `1px solid ${layer.color}20` }}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-mono text-xs font-bold" style={{ color: layer.color, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{layer.layer}</span>
                  <span style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: layer.color, direction: 'rtl' }}>{layer.arabic}</span>
                </div>
                <p className="font-body text-sm mb-3" style={{ color: 'rgba(245,240,232,0.6)', lineHeight: 1.7, fontSize: '0.82rem' }}>{layer.desc}</p>
                <div className="p-3 rounded" style={{ background: `${layer.color}08`, border: `1px solid ${layer.color}20`, borderLeft: `2px solid ${layer.color}40` }}>
                  <p className="font-mono text-xs italic" style={{ color: `${layer.color}80`, fontSize: '0.65rem', lineHeight: 1.6 }}>{layer.prompt}</p>
                </div>
              </div>
            ))}

            <Link
              href="/inner-reality-lens"
              className="group flex items-center justify-between p-4 rounded-xl transition-all duration-200"
              style={{ background: 'rgba(45,138,135,0.08)', border: '1px solid rgba(45,138,135,0.25)', textDecoration: 'none', minHeight: '48px' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(45,138,135,0.15)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(45,138,135,0.08)'; }}
            >
              <span className="font-body text-sm font-semibold" style={{ color: '#2d8a87' }}>Open the full IRL Lens →</span>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="transition-transform duration-300 group-hover:translate-x-1">
                <path d="M2 7h10M8 3l4 4-4 4" stroke="#2d8a87" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main ResourceGuideSection ─────────────────────────────────────────────────

export default function ResourceGuideSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [activeCity, setActiveCity] = useState<string>('chicago');
  const { counts, loading: cityLoading } = useCityObservationCounts();

  useEffect(() => {
    const elements = sectionRef.current?.querySelectorAll('.reveal-on-scroll');
    if (!elements) return;
    elements.forEach((el) => el.classList.add('hidden-initially'));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const city = CITY_PREVIEWS.find((c) => c.id === activeCity) ?? CITY_PREVIEWS[0];
  const observationCount = counts[activeCity] || 0;

  const CITY_WARD_SEVERITIES: Record<string, ('CRITICAL' | 'HIGH' | 'MODERATE')[]> = {
    chicago: ['CRITICAL', 'CRITICAL', 'HIGH', 'CRITICAL', 'HIGH', 'MODERATE', 'HIGH', 'CRITICAL'],
    atlanta: ['CRITICAL', 'CRITICAL', 'CRITICAL', 'CRITICAL', 'HIGH', 'HIGH', 'MODERATE', 'HIGH'],
    portland: ['CRITICAL', 'CRITICAL', 'HIGH', 'HIGH', 'MODERATE', 'HIGH', 'CRITICAL', 'HIGH'],
    detroit: ['HIGH', 'HIGH', 'CRITICAL', 'HIGH', 'MODERATE', 'HIGH', 'HIGH', 'CRITICAL'],
  };

  const wardSeverities = CITY_WARD_SEVERITIES[activeCity] || CITY_WARD_SEVERITIES.chicago;

  return (
    <section
      ref={sectionRef}
      id="resource-guide"
      className="relative overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, #0A1410 0%, #1C2B25 50%, #0F1E18 100%)',
        paddingTop: 'clamp(56px, 8vw, 96px)',
        paddingBottom: 'clamp(56px, 8vw, 96px)',
      }}
    >
      {/* Atmospheric glows */}
      <div className="absolute top-0 right-0 pointer-events-none" style={{ width: 'clamp(200px,40vw,400px)', height: 'clamp(200px,40vw,400px)', background: 'radial-gradient(circle, rgba(44,74,62,0.3) 0%, transparent 70%)', filter: 'blur(80px)' }} />
      <div className="absolute bottom-0 left-1/4 pointer-events-none" style={{ width: 'clamp(150px,25vw,300px)', height: 'clamp(150px,25vw,300px)', background: 'radial-gradient(circle, rgba(139,105,20,0.1) 0%, transparent 70%)', filter: 'blur(60px)' }} />

      <div className="max-w-[1280px] mx-auto px-6 lg:px-8">

        {/* ── HEADER ──────────────────────────────────────────────────── */}
        <div className="reveal-on-scroll stagger-1 mb-3 flex flex-wrap gap-2">
          <span className="font-mono text-xs tracking-widest uppercase px-4 py-2 rounded" style={{ color: '#7EC8A0', background: 'rgba(44,74,62,0.35)', border: '1px solid rgba(44,74,62,0.5)', fontFamily: 'var(--font-mono)' }}>
            Muslim Resource Guide · دليل الموارد
          </span>
        </div>

        <div className="reveal-on-scroll stagger-2 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-start mb-8">
          <div>
            <h2 className="font-heading mb-4" style={{ fontSize: 'clamp(1.6rem, 3.8vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.025em', color: '#F5F0E8', fontWeight: 600 }}>
              Connecting Communities{' '}
              <span style={{ color: '#B38A2A', fontStyle: 'italic' }}>Fisabilillah</span>
            </h2>
            <p className="font-body leading-relaxed mb-4" style={{ fontSize: 'clamp(0.9rem, 2vw, 1.05rem)', color: 'rgba(245,240,232,0.6)', lineHeight: 1.8 }}>
              The Muslim Resource Guide maps masajid, mutual aid, health, housing, and justice resources — understanding each as an Ayah pointing back to the One who provides. Every resource is a channel through which a Name of Allah operates in community.
            </p>
            <p className="font-body text-sm mb-5" style={{ color: 'rgba(245,240,232,0.4)', lineHeight: 1.7, fontStyle: 'italic', borderLeft: '2px solid rgba(126,200,160,0.3)', paddingLeft: 12 }}>
              "There is no creature on earth whose provision is not guaranteed by Allah." — Quran 11:6 · Ar-Razzaq
            </p>
            <Link
              href="/muslim-resource-guide"
              className="group inline-flex items-center gap-3 font-body text-sm font-semibold text-white px-7 py-4 rounded-btn transition-all duration-300"
              style={{ background: 'rgba(44,74,62,0.6)', border: '1px solid rgba(44,74,62,0.8)', minHeight: '48px', textDecoration: 'none' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(44,74,62,0.9)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(44,74,62,0.6)'; }}
            >
              Access The Full Resource Guide
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="transition-transform duration-300 group-hover:translate-x-1">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>

          {/* What the guide covers */}
          <div className="rounded-card p-5 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(28,43,37,0.9) 0%, rgba(15,30,24,0.95) 100%)', border: '1px solid rgba(44,74,62,0.4)' }}>
            <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: '#B38A2A' }}>What the guide covers</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Masajid & Prayer Spaces', icon: '🕌', tag: 'Community', color: '#7EC8A0' },
                { label: 'Halal Food & Mutual Aid', icon: '🌿', tag: 'Provision', color: '#C9A84C' },
                { label: 'Housing & Anti-Displacement', icon: '🏘', tag: 'Shelter', color: '#6BA8C4' },
                { label: 'Health & Wellbeing', icon: '🤲', tag: 'Healing', color: '#8a8ab8' },
                { label: 'Workforce & Reentry', icon: '🌱', tag: 'Empowerment', color: '#7EC8A0' },
                { label: 'Justice & Legal Aid', icon: '⚖', tag: 'Justice', color: '#C4956A' },
              ].map((cat) => (
                <div key={cat.label} className="flex items-center gap-2 p-2 rounded" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(44,74,62,0.25)' }}>
                  <span style={{ fontSize: 14 }}>{cat.icon}</span>
                  <div>
                    <p className="font-body" style={{ fontSize: '0.7rem', color: 'rgba(245,240,232,0.7)', lineHeight: 1.3 }}>{cat.label}</p>
                    <span className="font-mono" style={{ fontSize: '0.55rem', color: cat.color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{cat.tag}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── CHICAGO FULL MRG EXPERIENCE ──────────────────────────────── */}
        <div className="reveal-on-scroll stagger-3 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-mono text-xs tracking-widest uppercase px-3 py-1.5 rounded" style={{ color: '#7EC8A0', background: 'rgba(44,74,62,0.35)', border: '1px solid rgba(44,74,62,0.5)', fontSize: '0.6rem' }}>
              ◉ Live · Chicago, IL
            </span>
            <p className="font-body text-sm" style={{ color: 'rgba(245,240,232,0.4)', fontSize: '0.78rem' }}>
              A visiting caretaker's first introduction to the Muslim Resource Guide concept
            </p>
          </div>
          <ChicagoMRGExperience />
        </div>

        {/* ── OTHER CITIES ─────────────────────────────────────────────── */}
        <div className="reveal-on-scroll stagger-4">
          <p className="font-mono text-xs tracking-widest uppercase mb-3" style={{ color: 'rgba(245,240,232,0.25)', fontSize: '0.55rem' }}>
            Other Cities — Abbreviated Preview
          </p>
          <div className="flex flex-wrap gap-2 mb-5">
            {CITY_PREVIEWS.filter(c => c.id !== 'chicago').map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCity(activeCity === c.id ? 'chicago' : c.id)}
                className="font-mono text-xs tracking-wider uppercase px-4 py-3 rounded transition-all duration-200"
                style={{
                  minHeight: '48px',
                  background: activeCity === c.id ? c.color : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${activeCity === c.id ? c.accentColor : 'rgba(44,74,62,0.3)'}`,
                  color: activeCity === c.id ? c.accentColor : 'rgba(245,240,232,0.5)',
                  cursor: 'pointer',
                  letterSpacing: '0.08em',
                }}
              >
                <span style={{ marginRight: 6 }}>{c.arabicName}</span>
                {c.name}
              </button>
            ))}
          </div>

          {activeCity !== 'chicago' && !cityLoading && (() => {
            const c = CITY_PREVIEWS.find(x => x.id === activeCity)!;
            const ws = CITY_WARD_SEVERITIES[activeCity];
            return (
              <div key={activeCity} className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-1 flex flex-col gap-4">
                  <div className="rounded-card p-5 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${c.color} 0%, rgba(15,30,24,0.98) 100%)`, border: `1px solid ${c.accentColor}30` }}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className="font-mono text-xs tracking-widest uppercase" style={{ color: c.accentColor, fontSize: '0.6rem' }}>Key Ward · {c.wardCount} Wards</span>
                        <h3 className="font-heading mt-1" style={{ fontSize: 'clamp(0.9rem, 2vw, 1.1rem)', color: '#F5F0E8', fontWeight: 600 }}>{c.keyWard}</h3>
                      </div>
                      <span className="font-mono text-xs px-2 py-1 rounded" style={{ background: `${SEVERITY_COLORS[c.wardSeverity]}20`, border: `1px solid ${SEVERITY_COLORS[c.wardSeverity]}40`, color: SEVERITY_COLORS[c.wardSeverity], fontSize: '0.55rem', textTransform: 'uppercase' }}>
                        {c.wardSeverity}
                      </span>
                    </div>

                    {/* Ward stats mosaic — from actual cityData.ts indicators */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {c.wardStats.map((stat) => (
                        <div key={stat.label} className="p-2 rounded text-center" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                          <div style={{ fontSize: 16, marginBottom: 2 }}>{stat.icon}</div>
                          <div className="font-mono font-bold" style={{ fontSize: '0.85rem', color: c.accentColor }}>{stat.value}</div>
                          <div className="font-mono" style={{ fontSize: '0.5rem', color: 'rgba(245,240,232,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Masajid — from actual cityData.ts masajid arrays */}
                    <div>
                      <p className="font-mono text-xs mb-2" style={{ color: 'rgba(245,240,232,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.55rem' }}>
                        Masajid · {c.masajid.length} documented
                      </p>
                      {c.masajid.map((m) => (
                        <div key={m} className="flex items-center gap-2 mb-1">
                          <span style={{ color: c.accentColor, fontSize: 10 }}>🕌</span>
                          <span className="font-body" style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.65)' }}>{m}</span>
                        </div>
                      ))}
                    </div>

                    {/* Live observation count from Supabase irl_observations */}
                    {observationCount > 0 && (
                      <div className="mt-3 pt-3 flex items-center gap-2" style={{ borderTop: `1px solid ${c.accentColor}20` }}>
                        <span style={{ fontSize: 10, color: c.accentColor }}>◉</span>
                        <span className="font-mono" style={{ fontSize: '0.6rem', color: c.accentColor }}>
                          {observationCount} IRL observation{observationCount !== 1 ? 's' : ''} logged
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Ayat Layer — from cityData.ts resource category ayat */}
                  <div className="rounded-card p-5" style={{ background: 'rgba(28,43,37,0.4)', border: '1px solid rgba(44,74,62,0.35)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="font-mono text-xs px-2 py-1 rounded" style={{ background: `${c.accentColor}15`, border: `1px solid ${c.accentColor}30`, color: c.accentColor, fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {c.ayat.nameOfAllah} · {c.ayat.arabicName}
                      </span>
                    </div>
                    <p className="font-heading mb-2 text-right leading-relaxed" style={{ fontSize: 'clamp(0.9rem, 2vw, 1.1rem)', color: '#F5F0E8', direction: 'rtl', lineHeight: 1.8 }}>
                      {c.ayat.arabic}
                    </p>
                    <p className="font-mono text-xs mb-3" style={{ color: c.accentColor, fontSize: '0.6rem', letterSpacing: '0.06em' }}>
                      Quran {c.ayat.ref} · {c.ayat.meaning}
                    </p>
                    <p className="font-body italic" style={{ fontSize: '0.75rem', color: 'rgba(245,240,232,0.45)', lineHeight: 1.6, borderLeft: `2px solid ${c.accentColor}40`, paddingLeft: 10 }}>
                      "{c.openLetterQuote}"
                    </p>
                  </div>
                </div>

                {/* Right: Resources + City Description */}
                <div className="lg:col-span-2 flex flex-col gap-4">
                  <div className="rounded-card p-5" style={{ background: 'rgba(15,30,24,0.8)', border: '1px solid rgba(44,74,62,0.35)' }}>
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="font-heading" style={{ fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)', color: '#F5F0E8', fontWeight: 600 }}>{c.name}</h3>
                      <span className="font-heading" style={{ fontSize: 'clamp(0.9rem, 2vw, 1.1rem)', color: c.accentColor, opacity: 0.7 }}>{c.arabicName}</span>
                    </div>
                    <p className="font-body leading-relaxed mb-4" style={{ fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)', color: 'rgba(245,240,232,0.6)', lineHeight: 1.8 }}>
                      {c.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {c.tags.map((tag) => (
                        <span key={tag} className="font-mono px-3 py-1 rounded" style={{ background: `${c.accentColor}12`, border: `1px solid ${c.accentColor}25`, color: c.accentColor, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-card p-5" style={{ background: 'rgba(15,30,24,0.8)', border: '1px solid rgba(44,74,62,0.35)' }}>
                    <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: '#7EC8A0', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Key Resources — {c.name}
                    </p>
                    <div className="space-y-2">
                      {c.topResources.map((res) => (
                        <div key={res.name} className="flex items-center justify-between p-3 rounded transition-all duration-200" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(44,74,62,0.25)' }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(44,74,62,0.2)'; (e.currentTarget as HTMLElement).style.borderColor = `${c.accentColor}40`; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.03)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(44,74,62,0.25)'; }}
                        >
                          <div className="flex items-center gap-3">
                            <span style={{ fontSize: 16 }}>{res.icon}</span>
                            <span className="font-body text-sm" style={{ color: 'rgba(245,240,232,0.75)' }}>{res.name}</span>
                          </div>
                          <span className="font-mono text-xs px-2 py-1 rounded" style={{ background: `${c.accentColor}12`, color: c.accentColor, fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            {res.category}
                          </span>
                        </div>
                      ))}
                    </div>

                    <Link
                      href={`/muslim-resource-guide?city=${c.id}`}
                      className="group mt-4 flex items-center justify-between p-3 rounded transition-all duration-200"
                      style={{ background: `${c.accentColor}10`, border: `1px solid ${c.accentColor}30`, textDecoration: 'none', minHeight: '48px' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = `${c.accentColor}20`; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = `${c.accentColor}10`; }}
                    >
                      <span className="font-body text-sm font-semibold" style={{ color: c.accentColor }}>
                        View all {c.name} resources →
                      </span>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="transition-transform duration-300 group-hover:translate-x-1">
                        <path d="M2 7h10M8 3l4 4-4 4" stroke={c.accentColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Link>
                  </div>

                  {/* Ward Tile Mosaic — actual ward severity data from cityData.ts */}
                  <div className="rounded-card p-5" style={{ background: 'rgba(28,43,37,0.25)', border: '1px solid rgba(44,74,62,0.25)' }}>
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-mono text-xs tracking-widest uppercase" style={{ color: 'rgba(245,240,232,0.3)', fontSize: '0.55rem' }}>Ward Tile Mosaic · Severity Map · {c.wardCount} Wards</p>
                      <Link href={`/muslim-resource-guide?city=${c.id}`} className="font-mono" style={{ fontSize: '0.55rem', color: c.accentColor, textDecoration: 'none' }}>
                        Full Map →
                      </Link>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {ws.map((sev, i) => {
                        const opacity = 0.4 + (i % 3) * 0.2;
                        return (
                          <div
                            key={i}
                            className="rounded-sm"
                            title={`Ward ${i + 1}: ${sev}`}
                            style={{
                              width: `${22 + (i % 4) * 8}px`,
                              height: `${18 + (i % 3) * 6}px`,
                              background: SEVERITY_COLORS[sev],
                              opacity,
                              cursor: 'pointer',
                              transition: 'opacity 0.2s',
                            }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = String(opacity); }}
                          />
                        );
                      })}
                    </div>
                    <p className="font-mono mt-2" style={{ fontSize: '0.55rem', color: 'rgba(245,240,232,0.25)', letterSpacing: '0.06em' }}>
                      <span style={{ color: '#E05252' }}>■</span> CRITICAL &nbsp;
                      <span style={{ color: '#C9A84C' }}>■</span> HIGH &nbsp;
                      <span style={{ color: '#7EC8A0' }}>■</span> MODERATE
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* ── FOOTER CTA ───────────────────────────────────────────────── */}
        <div className="reveal-on-scroll stagger-5 mt-10 pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5" style={{ borderTop: '1px solid rgba(44,74,62,0.3)' }}>
          <div>
            <p className="font-heading mb-1" style={{ fontSize: 'clamp(1rem, 2.2vw, 1.3rem)', color: '#F5F0E8', fontWeight: 500 }}>
              Resources as Signs, Not Ends
            </p>
            <p className="font-body text-sm" style={{ color: 'rgba(245,240,232,0.45)', maxWidth: 480 }}>
              Every resource listed is understood as a sign — a means, not an end. The guide serves as a practical manifestation of the framework: Amanah made operational.
            </p>
          </div>
          <Link
            href="/muslim-resource-guide"
            className="group inline-flex items-center gap-3 font-body text-sm font-semibold text-white px-7 py-4 rounded-btn transition-all duration-300 flex-shrink-0"
            style={{ background: 'rgba(44,74,62,0.6)', border: '1px solid rgba(44,74,62,0.8)', minHeight: '48px', textDecoration: 'none' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(44,74,62,0.9)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(44,74,62,0.6)'; }}
          >
            Access The Resource Guide
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="transition-transform duration-300 group-hover:translate-x-1">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>

      </div>
    </section>
  );
}